(() => {
  const editorialHandles = new Set([
    'over-ons',
    'verkooppunten',
    'levering-verzendkosten',
    'levering-en-verzendkosten',
    'ruilen-retourneren',
    'ruilen-en-retourneren',
    'garantie-klachten',
    'garantie-en-klachten',
    'vragen',
    'privacyverklaring',
    'algemene-voorwaarden'
  ]);
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const pageHandle = pathParts[0] === 'pages' ? pathParts[1] : '';

  // The reseller page combines editable Shopify page content with a native
  // Shopify Forms app block. Keep both blocks intact so they can form one
  // compact conversion layout instead of splitting the introduction into
  // numbered editorial chapters.
  if (pageHandle === 'wederverkoper-worden') return;

  // These pages use the V071 editorial renderer for the alternating text layout.
  if (editorialHandles.has(pageHandle)) return;

  const main = document.querySelector("main[data-template^='page']");
  const storyRoot = main?.querySelector('[data-trots-page-story]');

  if (!main || !storyRoot) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const content = storyRoot.querySelector('[data-trots-page-content]');
  const formatter = content?.querySelector('rte-formatter');
  const editorActive = Boolean(window.Shopify?.designMode);

  const meaningfulNodes = (parent) =>
    [...parent.childNodes].filter(
      (node) => node.nodeType === Node.ELEMENT_NODE || node.textContent.trim()
    );

  const normalizeHeading = (heading) => {
    if (!(heading instanceof HTMLElement) || heading.tagName !== 'H1') return heading;

    const replacement = document.createElement('h2');
    [...heading.attributes].forEach((attribute) => {
      replacement.setAttribute(attribute.name, attribute.value);
    });
    replacement.innerHTML = heading.innerHTML;
    heading.replaceWith(replacement);

    return replacement;
  };

  const buildChapters = () => {
    if (!formatter || formatter.dataset.trotsStructured === 'true') return;

    const nodes = meaningfulNodes(formatter);
    if (!nodes.length) {
      storyRoot.hidden = true;
      return;
    }

    const groups = [];
    let group = [];

    nodes.forEach((node) => {
      const startsChapter = node instanceof HTMLElement && node.matches('h1, h2, h3');

      if (startsChapter && group.length) {
        groups.push(group);
        group = [];
      }

      group.push(node);
    });

    if (group.length) groups.push(group);

    if (groups.length === 1 && groups[0].length >= 4) {
      const onlyGroup = groups[0];
      const startsWithHeading =
        onlyGroup[0] instanceof HTMLElement && onlyGroup[0].matches('h1, h2, h3');

      if (startsWithHeading) {
        const heading = onlyGroup.shift();
        const firstChapter = [heading, onlyGroup.shift()].filter(Boolean);
        const paragraphChapters = [];

        while (onlyGroup.length) paragraphChapters.push(onlyGroup.splice(0, 2));
        groups.splice(0, 1, firstChapter, ...paragraphChapters);
      }
    }

    const fragment = document.createDocumentFragment();
    const colorCycle = ['white', 'soft', 'green'];

    groups.forEach((chapterNodes, index) => {
      const section = document.createElement('section');
      const inner = document.createElement('div');
      const intro = document.createElement('div');
      const body = document.createElement('div');
      const number = document.createElement('span');
      const firstNode = chapterNodes[0];
      const hasHeading = firstNode instanceof HTMLElement && firstNode.matches('h1, h2, h3');
      const bodyNodes = hasHeading ? chapterNodes.slice(1) : chapterNodes;
      const textLength = chapterNodes.reduce((total, node) => total + node.textContent.trim().length, 0);

      section.className = `trots-page-chapter trots-page-chapter--${colorCycle[index % colorCycle.length]}`;
      section.dataset.trotsPageSnap = '';
      section.setAttribute('aria-label', `Hoofdstuk ${index + 1}`);

      if (textLength > 1100) section.classList.add('trots-page-chapter--long');

      inner.className = 'trots-page-chapter__inner';
      intro.className = 'trots-page-chapter__intro';
      body.className = 'trots-page-chapter__body';
      number.className = 'trots-page-chapter__number';
      number.textContent = String(index + 1).padStart(2, '0');
      intro.append(number);

      if (hasHeading) {
        const heading = normalizeHeading(firstNode);
        heading.classList.add('trots-page-chapter__title');
        intro.append(heading);
      } else {
        intro.classList.add('trots-page-chapter__intro--compact');
      }

      bodyNodes.forEach((node) => body.append(node));

      const bodyElements = [...body.children];
      const leadParagraph = bodyElements.find((element) => element.tagName === 'P');
      if (leadParagraph?.textContent.trim().length <= 360) {
        leadParagraph.classList.add('trots-page-chapter__lead');
      }

      const lastParagraph = bodyElements.at(-1);
      if (
        lastParagraph?.tagName === 'P' &&
        lastParagraph.textContent.trim().length > 0 &&
        lastParagraph.textContent.trim().length <= 48
      ) {
        lastParagraph.classList.add('trots-page-signoff');
      }

      inner.append(intro, body);
      section.append(inner);
      fragment.append(section);
    });

    formatter.replaceChildren(fragment);
    formatter.dataset.trotsStructured = 'true';
  };

  buildChapters();

  const contactForm = main.querySelector('.contact-form');
  const contactSection = contactForm?.closest('.shopify-section');
  if (contactSection) {
    contactSection.classList.add('trots-page-contact-section');
    contactSection.dataset.trotsPageSnap = '';
  }

  if (editorActive || storyRoot.dataset.trotsPageScroll !== 'true' || reducedMotion.matches) return;

  let animationFrame;
  let isAnimating = false;
  let wheelCooldownUntil = 0;
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartScroll = 0;
  let touchTracking = false;
  let touchAllowsNativeScroll = false;
  let queuedDirection = 0;

  const getHeaderOffset = () =>
    Math.round(document.querySelector('header')?.getBoundingClientRect().height || 0);

  const getTargets = () => {
    const targets = [
      ...main.querySelectorAll('[data-trots-page-snap]'),
      document.querySelector('body > footer'),
    ].filter(Boolean);

    return [...new Set(targets)].filter((target) => !target.hidden && target.getClientRects().length);
  };

  const getPositions = () => {
    const headerOffset = getHeaderOffset();

    return getTargets().map((target) =>
      Math.max(0, Math.round(target.getBoundingClientRect().top + window.scrollY - headerOffset))
    );
  };

  const isInterfaceActive = (target) => {
    if (
      target instanceof Element &&
      target.closest('input, textarea, select, button, summary, [contenteditable="true"], dialog')
    ) {
      return true;
    }

    return [...document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"]')].some(
      (dialog) => dialog.getClientRects().length > 0
    );
  };

  const getCurrentTargetState = (current = window.scrollY) => {
    const targets = getTargets();
    const positions = getPositions();
    const tolerance = 12;
    const currentIndex = positions.reduce(
      (found, position, index) => (position <= current + tolerance ? index : found),
      0
    );
    const target = targets[currentIndex];
    const visibleHeight = window.innerHeight - getHeaderOffset();
    const targetHeight = target?.getBoundingClientRect().height || 0;
    const targetStart = positions[currentIndex] || 0;
    const targetEnd = Math.max(targetStart, targetStart + targetHeight - visibleHeight);

    return {
      currentIndex,
      isLong: targetHeight > visibleHeight + tolerance,
      positions,
      targetEnd,
      targetStart,
    };
  };

  const canScrollInsideCurrentTarget = (direction, current = window.scrollY) => {
    const { isLong, targetEnd, targetStart } = getCurrentTargetState(current);
    const tolerance = 12;

    if (!isLong) return false;

    return direction > 0
      ? current < targetEnd - tolerance
      : current > targetStart + tolerance;
  };

  const findDestination = (direction) => {
    const positions = getPositions();
    const current = window.scrollY;
    const tolerance = 12;

    if (direction > 0) return positions.find((position) => position > current + tolerance);

    return [...positions].reverse().find((position) => position < current - tolerance);
  };

  const animateTo = (destination) => {
    window.cancelAnimationFrame(animationFrame);

    const start = window.scrollY;
    const distance = destination - start;
    const duration = Math.min(720, Math.max(480, Math.abs(distance) * 0.62));
    const startTime = performance.now();

    if (Math.abs(distance) < 2) return;

    isAnimating = true;

    const step = (time) => {
      const progress = Math.min((time - startTime) / duration, 1);
      const eased = progress < 0.5
        ? 16 * progress ** 5
        : 1 - (-2 * progress + 2) ** 5 / 2;

      window.scrollTo(0, start + distance * eased);

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(step);
      } else {
        window.scrollTo(0, destination);
        isAnimating = false;

        if (queuedDirection) {
          const direction = queuedDirection;
          queuedDirection = 0;
          const nextDestination = findDestination(direction);
          if (nextDestination !== undefined) animateTo(nextDestination);
        }
      }
    };

    animationFrame = window.requestAnimationFrame(step);
  };

  const handleWheel = (event) => {
    if (event.ctrlKey || isInterfaceActive(event.target)) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || Math.abs(event.deltaY) < 4) return;

    if (isAnimating || performance.now() < wheelCooldownUntil) {
      event.preventDefault();
      return;
    }

    const direction = Math.sign(event.deltaY);
    if (canScrollInsideCurrentTarget(direction)) return;

    const destination = findDestination(direction);
    if (destination === undefined) return;

    event.preventDefault();
    wheelCooldownUntil = performance.now() + 380;
    animateTo(destination);
  };

  const handleKeydown = (event) => {
    if (isInterfaceActive(event.target)) return;

    const direction = ['PageDown', 'ArrowDown'].includes(event.key)
      ? 1
      : ['PageUp', 'ArrowUp'].includes(event.key)
        ? -1
        : 0;

    if (!direction || isAnimating || canScrollInsideCurrentTarget(direction)) return;

    const destination = findDestination(direction);
    if (destination === undefined) return;

    event.preventDefault();
    animateTo(destination);
  };

  const handleTouchStart = (event) => {
    touchTracking = false;
    touchAllowsNativeScroll = false;

    if (event.touches.length !== 1 || isInterfaceActive(event.target)) return;

    touchStartX = event.touches[0].clientX;
    touchStartY = event.touches[0].clientY;
    touchStartScroll = window.scrollY;

    if (isAnimating) return;
    touchTracking = true;
  };

  const handleTouchMove = (event) => {
    if (isAnimating) {
      event.preventDefault();
      return;
    }

    if (!touchTracking || event.touches.length !== 1) return;

    const deltaX = touchStartX - event.touches[0].clientX;
    const deltaY = touchStartY - event.touches[0].clientY;

    if (Math.abs(deltaY) < 8 || Math.abs(deltaY) <= Math.abs(deltaX) * 1.15) return;

    touchAllowsNativeScroll = canScrollInsideCurrentTarget(Math.sign(deltaY), touchStartScroll);
    if (!touchAllowsNativeScroll) event.preventDefault();
  };

  const handleTouchEnd = (event) => {
    if (event.changedTouches.length !== 1) return;

    const deltaX = touchStartX - event.changedTouches[0].clientX;
    const deltaY = touchStartY - event.changedTouches[0].clientY;
    const direction = Math.sign(deltaY);

    if (isAnimating) {
      if (Math.abs(deltaY) >= 44 && Math.abs(deltaY) > Math.abs(deltaX) * 1.15) {
        queuedDirection = direction;
      }
      return;
    }

    if (!touchTracking) return;
    touchTracking = false;

    if (Math.abs(deltaY) < 44 || Math.abs(deltaY) <= Math.abs(deltaX) * 1.15) return;

    const state = getCurrentTargetState(touchStartScroll);

    if (touchAllowsNativeScroll && state.isLong) {
      const boundary = direction > 0 ? state.targetEnd : state.targetStart;
      if (Math.abs(window.scrollY - boundary) <= 30) animateTo(boundary);
      return;
    }

    const destination = state.positions[state.currentIndex + direction];
    if (destination === undefined) return;

    wheelCooldownUntil = performance.now() + 340;
    animateTo(destination);
  };

  window.addEventListener('wheel', handleWheel, { passive: false });
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('touchstart', handleTouchStart, { passive: true });
  window.addEventListener('touchmove', handleTouchMove, { passive: false });
  window.addEventListener('touchend', handleTouchEnd, { passive: true });
})();
